import React, { useState, useEffect } from "react";
import {
    Container,
    Typography,
    Paper,
    Box,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    CircularProgress,
    Alert,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Card,
    CardContent,
    Grid,
    Avatar,
    InputAdornment,
    Divider,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
    Payments,
    History,
    Info,
    Add,
    CheckCircle,
    Warning,
    Error as ErrorIcon,
    Search,
    Refresh,
    Business,
    LocalHospital,
    CalendarToday,
    AttachMoney,
    Receipt,
    Notes,
    TrendingUp,
    TrendingDown,
} from "@mui/icons-material";
import {
    getAllSupplierPayments,
    getSupplierPaymentSummary,
    recordSupplierPayment
} from "../services/api";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

export default function SupplierPaymentsPage() {
    const theme = useTheme();
    const { t } = useTranslation();
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [summary, setSummary] = useState({
        overdueCount: 0,
        pendingCount: 0,
        totalOwed: 0,
    });

    const [openPayModal, setOpenPayModal] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [payFormData, setPayFormData] = useState({
        amount: "",
        paymentMethod: "cash",
        transactionReference: "",
        notes: "",
    });

    const fetchPaymentsData = async () => {
        try {
            setLoading(true);
            setError("");

            const [paymentsRes, summaryRes] = await Promise.all([
                getAllSupplierPayments(),
                getSupplierPaymentSummary()
            ]);

            setPayments(paymentsRes.data.payments || []);
            setSummary(summaryRes.data.summary || {
                overdueCount: 0,
                pendingCount: 0,
                totalOwed: 0,
            });

        } catch (err) {
            console.error("Error fetching supplier payments:", err);
            setError("Failed to load supplier payment data.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPaymentsData();
    }, []);

    const handleOpenPayModal = (payment) => {
        setSelectedPayment(payment);
        setPayFormData({
            amount: payment.remainingAmount,
            paymentMethod: "cash",
            transactionReference: "",
            notes: "",
        });
        setOpenPayModal(true);
    };

    const handleRecordPayment = async () => {
        if (!payFormData.amount || payFormData.amount <= 0) {
            toast.error("Please enter a valid amount");
            return;
        }

        try {
            await recordSupplierPayment(selectedPayment.id, payFormData);
            toast.success("Payment recorded successfully");
            setOpenPayModal(false);
            fetchPaymentsData();
        } catch (err) {
            const msg = err.response?.data?.message || "Failed to record payment";
            toast.error(`❌ ${msg}`);
        }
    };

    const getStatusChip = (status) => {
        switch (status) {
            case "paid":
                return <Chip label="PAID" color="success" size="small" icon={<CheckCircle />} />;
            case "overdue":
                return <Chip label="OVERDUE" color="error" size="small" icon={<ErrorIcon />} />;
            case "partial":
                return <Chip label="PARTIAL" color="warning" size="small" icon={<Warning />} />;
            default:
                return <Chip label="PENDING" color="info" size="small" icon={<Info />} />;
        }
    };

    const filteredPayments = payments.filter((p) => {
        const matchesSearch =
            p.Supplier?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            p.Inventory?.batchNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
            p.Inventory?.Medicine?.name.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus = statusFilter === "all" || p.paymentStatus === statusFilter;

        return matchesSearch && matchesStatus;
    });

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 6 }}>
            {/* Header */}
            <Box
                sx={{
                    textAlign: "center",
                    mb: 6,
                    background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.primary.main} 100%)`,
                    borderRadius: 4,
                    py: 4,
                    px: 3,
                    color: 'white',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
                }}
            >
                <Payments sx={{ fontSize: 48, mb: 2, opacity: 0.9 }} />
                <Typography variant="h3" fontWeight="bold" gutterBottom>
                    Supplier Payments
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9 }}>
                    Manage your credit and cash purchases from suppliers
                </Typography>
            </Box>

            {/* Summary Stats */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={4}>
                    <Card sx={{ borderRadius: 3, bgcolor: alpha(theme.palette.error.main, 0.05) }}>
                        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: 'error.main' }}>
                                <ErrorIcon />
                            </Avatar>
                            <Box>
                                <Typography variant="h5" fontWeight="bold" color="error">
                                    {summary.overdueCount}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Overdue Payments
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Card sx={{ borderRadius: 3, bgcolor: alpha(theme.palette.warning.main, 0.05) }}>
                        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: 'warning.main' }}>
                                <Warning />
                            </Avatar>
                            <Box>
                                <Typography variant="h5" fontWeight="bold" color="warning.dark">
                                    {summary.pendingCount}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Pending/Partial Payments
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Card sx={{ borderRadius: 3, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: 'primary.main' }}>
                                <AttachMoney />
                            </Avatar>
                            <Box>
                                <Typography variant="h5" fontWeight="bold" color="primary">
                                    ETB {parseFloat(summary.totalOwed).toFixed(2)}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Total Outstanding Owed
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Filters */}
            <Card sx={{ mb: 4, borderRadius: 3 }}>
                <CardContent sx={{ p: 3 }}>
                    <Grid container spacing={3} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Search Supplier, Medicine or Batch"
                                fullWidth
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={statusFilter}
                                    label="Status"
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <MenuItem value="all">All Status</MenuItem>
                                    <MenuItem value="pending">Pending</MenuItem>
                                    <MenuItem value="partial">Partial</MenuItem>
                                    <MenuItem value="paid">Paid</MenuItem>
                                    <MenuItem value="overdue">Overdue</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Button
                                variant="outlined"
                                startIcon={<Refresh />}
                                fullWidth
                                onClick={fetchPaymentsData}
                                sx={{ height: '56px' }}
                            >
                                Refresh
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Table */}
            <TableContainer component={Paper} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                <Table>
                    <TableHead sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.05) }}>
                        <TableRow>
                            <TableCell><strong>Supplier & Medicine</strong></TableCell>
                            <TableCell><strong>Type</strong></TableCell>
                            <TableCell align="right"><strong>Total Amount</strong></TableCell>
                            <TableCell align="right"><strong>Paid</strong></TableCell>
                            <TableCell align="right"><strong>Remaining</strong></TableCell>
                            <TableCell><strong>Due Date</strong></TableCell>
                            <TableCell><strong>Status</strong></TableCell>
                            <TableCell align="center"><strong>Actions</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredPayments.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                                    <Typography variant="h6" color="textSecondary">No payment records found.</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredPayments.map((p) => (
                                <TableRow key={p.id}>
                                    <TableCell>
                                        <Box>
                                            <Typography fontWeight="bold">{p.Supplier?.name}</Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                {p.Inventory?.Medicine?.name} ({p.Inventory?.batchNumber})
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={p.paymentType.toUpperCase()}
                                            size="small"
                                            variant="outlined"
                                            color={p.paymentType === 'cash' ? 'default' : 'secondary'}
                                        />
                                    </TableCell>
                                    <TableCell align="right">ETB {parseFloat(p.totalAmount).toFixed(2)}</TableCell>
                                    <TableCell align="right">{parseFloat(p.paidAmount).toFixed(2)}</TableCell>
                                    <TableCell align="right">
                                        <Typography fontWeight="bold" color={p.remainingAmount > 0 ? "error" : "success.main"}>
                                            {parseFloat(p.remainingAmount).toFixed(2)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <CalendarToday sx={{ fontSize: 16, color: 'text.secondary' }} />
                                            <Typography variant="body2">
                                                {p.dueDate ? new Date(p.dueDate).toLocaleDateString() : 'N/A'}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>{getStatusChip(p.paymentStatus)}</TableCell>
                                    <TableCell align="center">
                                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                            <Tooltip title="View Details/History">
                                                <IconButton color="info" size="small"><History /></IconButton>
                                            </Tooltip>
                                            {p.remainingAmount > 0 && (
                                                <Tooltip title="Record Payment">
                                                    <Button
                                                        variant="contained"
                                                        color="success"
                                                        size="small"
                                                        onClick={() => handleOpenPayModal(p)}
                                                    >
                                                        Pay
                                                    </Button>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Record Payment Modal */}
            <Dialog
                open={openPayModal}
                onClose={() => setOpenPayModal(false)}
                fullWidth
                maxWidth="xs"
                PaperProps={{
                    sx: {
                        borderRadius: 4,
                        boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
                        backdropFilter: 'blur(10px)',
                        backgroundColor: alpha(theme.palette.background.paper, 0.95),
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                    }
                }}
            >
                <DialogTitle sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 3,
                    background: `linear-gradient(to right, ${alpha(theme.palette.success.main, 0.05)}, transparent)`,
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`
                }}>
                    <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: 'success.main' }}>
                        <Payments />
                    </Avatar>
                    <Typography variant="h6" fontWeight="bold">Record Payment</Typography>
                </DialogTitle>
                <DialogContent sx={{ mt: 2, p: 3 }}>
                    <Box sx={{ mb: 3, p: 2, bgcolor: alpha(theme.palette.secondary.main, 0.05), borderRadius: 3, border: `1px dashed ${alpha(theme.palette.secondary.main, 0.2)}` }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="body2" color="textSecondary">Supplier:</Typography>
                            <Typography variant="body1" fontWeight="bold">{selectedPayment?.Supplier?.name}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" color="textSecondary">Outstanding:</Typography>
                            <Typography variant="h6" fontWeight="bold" color="error.main">ETB {selectedPayment?.remainingAmount}</Typography>
                        </Box>
                    </Box>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <TextField
                                label="Amount to Pay"
                                type="number"
                                fullWidth
                                value={payFormData.amount}
                                onChange={(e) => setPayFormData({ ...payFormData, amount: e.target.value })}
                                InputProps={{ startAdornment: <InputAdornment position="start">ETB</InputAdornment> }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Method</InputLabel>
                                <Select
                                    value={payFormData.paymentMethod}
                                    label="Method"
                                    onChange={(e) => setPayFormData({ ...payFormData, paymentMethod: e.target.value })}
                                >
                                    <MenuItem value="cash">Cash</MenuItem>
                                    <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                                    <MenuItem value="check">Check</MenuItem>
                                    <MenuItem value="mobile_money">Mobile Money</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Reference (Optional)"
                                fullWidth
                                value={payFormData.transactionReference}
                                onChange={(e) => setPayFormData({ ...payFormData, transactionReference: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Internal Notes"
                                fullWidth
                                multiline
                                rows={2}
                                value={payFormData.notes}
                                onChange={(e) => setPayFormData({ ...payFormData, notes: e.target.value })}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button
                        onClick={() => setOpenPayModal(false)}
                        variant="outlined"
                        sx={{ borderRadius: 2, px: 3 }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleRecordPayment}
                        variant="contained"
                        color="success"
                        startIcon={<CheckCircle />}
                        sx={{
                            px: 4,
                            py: 1.5,
                            borderRadius: 2,
                            background: `linear-gradient(45deg, ${theme.palette.success.main} 30%, ${theme.palette.success.dark} 90%)`,
                            boxShadow: '0 3px 15px rgba(46, 125, 50, 0.3)',
                            '&:hover': {
                                boxShadow: '0 5px 20px rgba(46, 125, 50, 0.4)',
                            }
                        }}
                    >
                        Confirm Payment
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}
